import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { parseSearchParams } from '@/utils/stringUtils';
import { detokenize } from '@/utils/tokenizer';

interface UsePostHogDistinctIdResult {
  distinctId: string | undefined;
  loading: boolean;
}

/**
 * Hook to get PostHog distinct ID from URL params.
 * Supports both legacy ph_distinct_id and new ph_token.
 * ph_distinct_id takes precedence (backward compatibility).
 */
export const usePostHogDistinctId = (): UsePostHogDistinctIdResult => {
  const location = useLocation();
  const searchParams = parseSearchParams(location.search);

  const urlDistinctId = searchParams.ph_distinct_id?.trim();
  const urlToken = searchParams.ph_token?.trim();

  const [distinctId, setDistinctId] = useState<string | undefined>(urlDistinctId);
  const [loading, setLoading] = useState<boolean>(!urlDistinctId && !!urlToken);

  useEffect(() => {
    // ph_distinct_id takes precedence (backward compatibility)
    if (urlDistinctId) {
      setDistinctId(urlDistinctId);
      setLoading(false);
      return;
    }

    // If no token, nothing to do
    if (!urlToken) {
      return;
    }

    // Detokenize the ph_token
    const fetchDistinctId = async () => {
      setLoading(true);

      const result = await detokenize(urlToken);

      if (result.success && result.value) {
        setDistinctId(result.value);
      } else {
        // eslint-disable-next-line no-console
        console.error('Detokenization failed:', result.error || 'Unknown error');
      }

      setLoading(false);
    };

    fetchDistinctId();
  }, [urlDistinctId, urlToken]);

  return { distinctId, loading };
};
